import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SelectItem } from 'primeng/api';

import { JobsService } from '@app/shared/jobs.service';
import { SearchComponent } from '@app/shared/search/search.component';
import { Job, JobFilter } from '@app/shared/shared.models';

@Component({
  styleUrls: ['./jobs.component.css'],
  templateUrl: './jobs.component.html',
})
export class JobsComponent implements OnDestroy {
  division: string;
  divisions: SelectItem[];
  driverTypes: SelectItem[];
  experience: string;
  experiences: SelectItem[];
  filter: JobFilter;
  homeTimes: SelectItem[];
  isLoading = false;
  isUniquePage: boolean;
  jobs: Job[] = [];
  @ViewChild('searchComponent') searchComponent: SearchComponent;
  selectedDivisions: string[] = [];
  selectedDriverTypes: string[] = [];
  selectedExperiences: number[] = [];
  selectedHomeTimes: string[] = [];
  stateZip: string;
  title: string;

  private navigationSubscription: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private jobsService: JobsService,
    private router: Router
  ) {
    this.navigationSubscription = this.router.events.subscribe((event: any) => {
      // We will need the following if i am current for example on /jobs;stateZip=California and then
      // the user clicks over browse jobs
      if (event instanceof NavigationEnd) {
        this.isUniquePage = false;
        let hasParameters = false;
        this.division = null;
        this.experience = null;
        this.clearFilters();

        this.stateZip = this.activatedRoute.snapshot.paramMap.get('stateZip');
        if (this.searchComponent) {
          this.searchComponent.selectedValue = this.stateZip;
        }
        const divisionParameter = this.activatedRoute.snapshot.paramMap.get('division');
        const experienceParameter = this.activatedRoute.snapshot.paramMap.get('experience');
        if (experienceParameter) {
          const experienceNumber = parseInt(experienceParameter, 10);
          this.selectedExperiences.push(experienceNumber);
          this.isUniquePage = true;
          this.experience = experienceParameter;
        }
        if (divisionParameter) {
          this.isUniquePage = true;
          this.division = divisionParameter;
          this.selectedDivisions.push(divisionParameter);
        }
        if (this.stateZip || divisionParameter || experienceParameter) {
          hasParameters = true;
        }
        if (!hasParameters) {
          this.clearFilters();
        }
        this.applyFilters();
      }
    });

    this.divisions = [
      { label: 'Dedicated', value: 'DEDICT' },
      { label: 'Intermodal', value: 'INTMDL' },
      { label: 'Instructor', value: 'SCHL' },
      { label: 'Over the Road', value: 'OVER_THE_ROAD' },
      { label: 'Mexico', value: 'MEXICO' },
      { label: 'Other', value: 'OTHER' },
    ];
    this.driverTypes = [
      { label: 'Solo', value: 'SOLO' },
      { label: 'Team', value: 'TEAM' },
      { label: 'Trainer', value: 'TRAIN' },
    ];
    this.experiences = [
      { label: 'Need CDL', value: 0 },
      { label: 'Less than 3 months', value: 2 },
      { label: 'More than 3 months', value: 3 },
    ];
    this.homeTimes = [
      { label: 'Home daily', value: 'Home daily' },
      { label: 'Home weekly', value: 'Home weekly' },
      { label: 'Home every 2 weeks', value: 'Home every 2 weeks' },
      { label: 'Home once a month', value: 'Home once a month' },
    ];
  }

  filtersChanged(event: any) {
    if (this.arefiltersClean()) {
      this.clearFilters();
    }
    this.applyFilters();
  }

  goToOverview(id: string, city: string, state: string) {
    this.router.navigate([`overview/${id}`, { city, state }]);
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  onClear() {
    this.clearFilters();
    this.applyFilters();
  }

  onSearch(search: string) {
    if (typeof search === 'string') {
      this.stateZip = search;
      this.applyFilters();
      if (search !== '') {
        if (this.division) {
          this.router.navigate([`jobs/`, { stateZip: search, division: this.division }]);
        } else {
          if (this.experience) {
            this.router.navigate([`jobs/`, { stateZip: search, experience: this.experience }]);
          } else {
            this.router.navigate([`jobs/`, { stateZip: search }]);
          }
        }
      }
    }
  }

  private applyFilters() {
    this.filter = {
      divisions: [],
      driverTypes: [],
      experiences: [],
      hometimes: [],
      location: '',
    } as JobFilter;

    this.filter.divisions = this.selectedDivisions;
    this.filter.experiences = this.selectedExperiences;
    this.filter.hometimes = this.selectedHomeTimes;
    this.filter.driverTypes = this.selectedDriverTypes;
    this.filter.location = this.stateZip === '' ? '' : this.stateZip;
    this.isLoading = true;
    this.jobsService
      .jobsFilter(this.filter)
      .then((apiJobs) => {
        this.jobs = apiJobs;
        this.setTitle();
      })
      .catch(() => {
        this.jobs = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private arefiltersClean() {
    return (
      this.selectedDivisions.length === 0 &&
      this.selectedDriverTypes.length === 0 &&
      this.selectedExperiences.length === 0 &&
      this.selectedHomeTimes.length === 0 &&
      !this.stateZip
    );
  }

  private clearFilters() {
    this.selectedDriverTypes = [];
    this.selectedHomeTimes = [];
    if (this.isUniquePage && this.division) {
      this.selectedExperiences = [];
    }
    if (this.isUniquePage && this.experience) {
      this.selectedDivisions = [];
    }
    if (!this.isUniquePage) {
      this.selectedExperiences = [];
      this.selectedDivisions = [];
    }
  }

  private isZipCode(zip: string) {
    const numbers = /^[0-9]+$/;
    return zip.match(numbers);
  }

  private setTitle() {
    // Inspect if the stateZip parameter has something.
    const jobCount = this.jobs.length;
    if (this.stateZip) {
      if (this.isZipCode(this.stateZip)) {
        this.title = `${jobCount} Jobs Near ${this.stateZip}`;
      } else {
        this.title = `${jobCount} ${this.stateZip} Jobs`;
      }
      return;
    }

    // We need to inspect if there was a division selected
    if (this.division) {
      this.title = `${jobCount} Jobs`;
      return;
    }

    if (this.arefiltersClean()) {
      this.title = `All ${jobCount} Jobs`;
      return;
    } else {
      this.title = `${jobCount} Jobs`;
      return;
    }
  }
}
